import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Truck, RotateCcw, MessageCircle, ChevronRight, Ruler } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useStore, money, waLink } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/store/ProductCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { settings } = useStore();
  const { addItem } = useCart();
  const { wishlist, toggleWishlist } = useAuth();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [img, setImg] = useState(0);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [pincode, setPincode] = useState("");
  const [delivery, setDelivery] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setColor(r.data.colors?.[0]?.name || null);
      setImg(0);
    }).catch(() => nav("/shop"));
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug, nav]);

  if (!p) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-sharoa-muted">Loading…</div>;

  const variant = (p.variants || []).find((v) => v.color === color && v.size === size);
  const colorVariants = (p.variants || []).filter((v) => v.color === color);
  const discount = p.mrp && p.price < p.mrp ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const inWish = wishlist?.includes(p.id);
  const wa = settings?.social?.whatsapp;
  const sizeChart = settings?.size_chart || {};

  const buildItem = () => {
    if (!size) { toast.error("Please select a size"); return null; }
    if (!variant || variant.stock < qty) { toast.error("This size is out of stock"); return null; }
    return { product_id: p.id, sku: variant.sku, name: p.name, slug: p.slug,
      image: p.images?.[0], size, color, price: variant.sale_price || variant.price, quantity: qty };
  };
  const addToBag = () => { const it = buildItem(); if (it) addItem(it); };
  const buyNow = () => { const it = buildItem(); if (it) { addItem(it, false); nav("/checkout"); } };

  const checkPin = async () => {
    try { const r = await api.get(`/delivery/check?pincode=${pincode}`); setDelivery(r.data); }
    catch (e) { setDelivery(null); toast.error(apiErr(e)); }
  };

  const waMsg = `${wa?.greeting || "Hi SHAROA ♡ I'm interested in"} the ${p.name}${color ? ` in ${color}` : ""}${size ? `, size ${size}` : ""} (${money(p.price)}). Can you help me? ${window.location.href}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
      <div className="text-xs text-sharoa-muted mb-6 flex items-center gap-1">
        <Link to="/" className="hover:text-sharoa-rose">Home</Link><ChevronRight size={12} />
        <Link to={`/shop/${p.category}`} className="hover:text-sharoa-rose capitalize">{p.category?.replace(/-/g, " ")}</Link><ChevronRight size={12} />
        <span className="text-sharoa-charcoal">{p.name}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-14">
        {/* GALLERY */}
        <div className="flex gap-3">
          <div className="hidden sm:flex flex-col gap-3">
            {(p.images || []).map((src, i) => (
              <button key={i} onClick={() => setImg(i)} className={`w-16 h-20 overflow-hidden border ${img === i ? "border-sharoa-rose" : "border-sharoa-line"}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1">
            <div className="aspect-[3/4] overflow-hidden bg-sharoa-ivory group">
              <img src={p.images?.[img]} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 cursor-zoom-in" />
            </div>
            {p.video && <video src={p.video} controls className="w-full mt-3" />}
            <div className="flex sm:hidden gap-2 mt-3">
              {(p.images || []).map((src, i) => (
                <button key={i} onClick={() => setImg(i)} className={`w-14 h-16 overflow-hidden border ${img === i ? "border-sharoa-rose" : "border-sharoa-line"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* INFO */}
        <div>
          {p.is_new && <span className="text-[10px] uppercase tracking-widest bg-sharoa-charcoal text-white px-2.5 py-1">New</span>}
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">{p.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <StarRating value={p.rating} /><span className="text-xs text-sharoa-muted">{p.rating} · {p.review_count} reviews</span>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <span className="font-mono text-2xl text-sharoa-charcoal">{money(p.price)}</span>
            {discount > 0 && <><span className="font-mono text-base text-sharoa-muted line-through">{money(p.mrp)}</span><span className="text-sharoa-rose text-sm font-medium">{discount}% off</span></>}
          </div>
          <p className="text-xs text-sharoa-muted mt-1">MRP incl. of all taxes</p>
          <p className="text-sharoa-charcoal/80 mt-5 leading-relaxed text-sm">{p.description}</p>

          {/* COLOR */}
          <div className="mt-7">
            <p className="text-xs uppercase tracking-widest font-semibold mb-2">Colour: <span className="text-sharoa-muted normal-case font-normal">{color}</span></p>
            <div className="flex gap-2">
              {(p.colors || []).map((c) => (
                <button key={c.name} data-testid={`color-${c.name.replace(/\s/g,"-")}`} onClick={() => { setColor(c.name); const nv = (p.variants || []).find((x) => x.color === c.name && x.size === size); if (!nv || nv.stock === 0) setSize(null); }} title={c.name}
                  className={`w-9 h-9 rounded-full border-2 ${color === c.name ? "border-sharoa-rose" : "border-sharoa-line"}`} style={{ background: c.hex }} />
              ))}
            </div>
          </div>

          {/* SIZE */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest font-semibold">Size</p>
              <Dialog>
                <DialogTrigger asChild><button data-testid="size-guide-btn" className="text-xs text-sharoa-rose flex items-center gap-1 sh-underline"><Ruler size={13} /> Size Guide ♡</button></DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader><DialogTitle className="font-serif text-2xl">Size Guide</DialogTitle>
                    <DialogDescription className="text-xs text-sharoa-muted">Approximate bust measurements. Fits true to size ♡</DialogDescription></DialogHeader>
                  <table className="w-full text-sm mt-2">
                    <thead><tr className="border-b border-sharoa-line text-left"><th className="py-2">Size</th><th>Bust</th></tr></thead>
                    <tbody>{Object.entries(sizeChart).map(([k, v]) => <tr key={k} className="border-b border-sharoa-line/60"><td className="py-2 font-medium">{k}</td><td>{v}</td></tr>)}</tbody>
                  </table>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex flex-wrap gap-2">
              {(p.sizes || []).map((s) => {
                const v = colorVariants.find((x) => x.size === s);
                const oos = !v || v.stock === 0;
                return (
                  <button key={s} data-testid={`size-${s}`} disabled={oos} onClick={() => setSize(s)}
                    className={`min-w-[48px] h-11 px-3 text-sm border relative ${size === s ? "bg-sharoa-charcoal text-white border-sharoa-charcoal" : "border-sharoa-line text-sharoa-charcoal"} ${oos ? "opacity-40 line-through cursor-not-allowed" : ""}`}>{s}</button>
                );
              })}
            </div>
            {variant && <p className="text-xs mt-2 text-sharoa-muted">{variant.stock > 0 ? (variant.stock < 5 ? `Only ${variant.stock} left ♡` : "In stock") : "Out of stock"}</p>}
          </div>

          {/* QTY + CTA */}
          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center border border-sharoa-line">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3">−</button>
              <span className="px-3 font-mono">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3">+</button>
            </div>
            <button data-testid="wishlist-btn" onClick={() => toggleWishlist(p.id)} className="w-12 h-12 border border-sharoa-line flex items-center justify-center hover:border-sharoa-rose">
              <Heart size={18} className={inWish ? "fill-sharoa-rose text-sharoa-rose" : ""} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <button data-testid="add-to-bag-btn" onClick={addToBag} className="bg-sharoa-charcoal text-white text-xs uppercase tracking-[0.16em] py-4 hover:bg-black transition-colors">Add to Bag ♡</button>
            <button data-testid="buy-now-btn" onClick={buyNow} className="bg-sharoa-rose text-white text-xs uppercase tracking-[0.16em] py-4 hover:bg-sharoa-rosedark transition-colors">Buy Now</button>
          </div>
          <a href={waLink(wa?.number, waMsg)} target="_blank" rel="noreferrer" data-testid="product-whatsapp-btn"
            className="mt-3 flex items-center justify-center gap-2 border border-[#25D366] text-[#128C4A] text-xs uppercase tracking-widest py-3.5 hover:bg-[#25D366]/10 transition-colors">
            <MessageCircle size={16} /> Ask us on WhatsApp ♡
          </a>

          {/* PIN + returns */}
          <div className="mt-7 border-t border-sharoa-line pt-6 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold mb-2 flex items-center gap-2"><Truck size={15} /> Delivery</p>
              <div className="flex gap-2">
                <input data-testid="pincode-input" value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="Enter PIN code" maxLength={6}
                  className="flex-1 border border-sharoa-line px-3 py-2.5 text-sm outline-none focus:border-sharoa-rose" />
                <button data-testid="pincode-check-btn" onClick={checkPin} className="bg-sharoa-charcoal text-white text-xs uppercase tracking-widest px-5">Check</button>
              </div>
              {delivery && <p className="text-xs text-sharoa-success mt-2">✓ Serviceable · {delivery.message} · COD available</p>}
            </div>
            {p.non_returnable ? (
              <div className="bg-sharoa-ivory p-4">
                <p className="text-xs uppercase tracking-widest font-semibold text-sharoa-charcoal">Final Sale</p>
                <p className="text-xs text-sharoa-muted mt-1">This item is non-returnable.</p>
              </div>
            ) : (
              <div className="bg-sharoa-pale p-4">
                <p className="text-xs uppercase tracking-widest font-semibold text-sharoa-rose flex items-center gap-2"><RotateCcw size={14} /> 7-Day Returns ♡</p>
                <p className="text-xs text-sharoa-muted mt-1">Eligible items can be requested for return within 7 days of delivery, subject to SHAROA's Return Policy.</p>
                <Link to="/return-policy" className="text-xs text-sharoa-rose sh-underline mt-1 inline-block">View Return Policy</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <div className="mt-16 border-t border-sharoa-line pt-12">
        <h2 className="font-serif text-2xl sm:text-3xl mb-6">Reviews ({reviews.length})</h2>
        {reviews.length === 0 ? <p className="text-sharoa-muted text-sm">No reviews yet. Be the first ♡</p> : (
          <div className="grid sm:grid-cols-2 gap-5">
            {reviews.map((r) => (
              <div key={r.id} className="border border-sharoa-line p-5">
                <StarRating value={r.rating} />
                <h4 className="font-serif text-lg mt-2">{r.title}</h4>
                <p className="text-sm text-sharoa-muted mt-1">{r.body}</p>
                <p className="text-xs text-sharoa-charcoal mt-3">{r.name} {r.verified && <span className="text-sharoa-rose">· ✓ Verified Buyer</span>}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* mobile sticky add */}
      <div className="lg:hidden fixed bottom-16 inset-x-0 z-30 bg-white border-t border-sharoa-line p-3 flex gap-2">
        <button onClick={addToBag} className="flex-1 bg-sharoa-charcoal text-white text-xs uppercase tracking-widest py-3.5">Add to Bag ♡</button>
        <button onClick={buyNow} className="flex-1 bg-sharoa-rose text-white text-xs uppercase tracking-widest py-3.5">Buy Now</button>
      </div>
    </div>
  );
}
